import { zodResolver } from '@hookform/resolvers/zod';
import type { Subject } from '@lastrouter/contracts';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Field, Input, NativeSelect, Switch, Textarea } from '@lastrouter/ui';
import { Plus, Trash2 } from '@lastrouter/ui';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { Controller, useFieldArray, useForm, type Control, type UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { splitList } from '../../lib/format.js';

/*
 * Edição de um assunto do catálogo. O formulário usa texto livre onde o arquivo usa listas
 * (sinônimos, passos) e converte na volta, preservando o que o formulário não mostra
 * (formatos de período, exemplos extras, aprovações). Item alterado ganha versão nova.
 */

const ID_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;

export function subjectFormSchema(t: TFunction) {
  const req = z.string().trim().min(1, t('common.required'));
  const id = z.string().trim().regex(ID_RE, t('context.form.idHelp'));
  return z.object({
    nome: req,
    descricao: z.string(),
    fonte: id,
    app: req,
    donos: z.object({ negocio: req, tecnico: req }),
    metricas: z.array(z.object({ id, nome: req, descricao: req, sinonimos: z.string(), unidade: req, expressao: req })).min(1, t('common.required')),
    dimensoes: z.array(z.object({ id, nome: req, campo: req, tipo: z.enum(['texto', 'periodo_mensal', 'ano', 'data']), sensivel: z.boolean(), sinonimos: z.string() })),
    queries: z.array(
      z.object({
        id,
        exemplo: req,
        metricas: req,
        dimensoes: z.string(),
        certificada_por: z.string(),
        parametros: z.array(z.object({ nome: id, dimensao: id, obrigatorio: z.boolean() })),
      }),
    ),
    roteiros: z.array(z.object({ id, quando: req, passos: req })),
  });
}
export type SubjectForm = z.infer<ReturnType<typeof subjectFormSchema>>;

export function toForm(s: Subject): SubjectForm {
  return {
    nome: s.nome,
    descricao: s.descricao ?? '',
    fonte: s.fonte,
    app: s.app,
    donos: { ...s.donos },
    metricas: s.metricas.map((m) => ({ id: m.id, nome: m.nome, descricao: m.descricao, sinonimos: m.sinonimos.join(', '), unidade: m.unidade, expressao: m.expressao })),
    dimensoes: s.dimensoes.map((d) => ({ id: d.id, nome: d.nome, campo: d.campo, tipo: d.tipo, sensivel: d.sensivel, sinonimos: d.sinonimos.join(', ') })),
    queries: s.queries_validadas.map((q) => ({
      id: q.id,
      exemplo: q.exemplo,
      metricas: q.metricas.join(', '),
      dimensoes: q.dimensoes.join(', '),
      certificada_por: q.certificada_por ?? '',
      parametros: Object.entries(q.parametros).map(([nome, p]) => ({ nome, dimensao: p.dimensao, obrigatorio: p.obrigatorio })),
    })),
    roteiros: s.roteiros.map((r) => ({ id: r.id, quando: r.quando, passos: r.passos.join('\n') })),
  };
}

/** Versão nova só quando o conteúdo do item mudou. */
function versioned<T extends { id: string; versao: number }>(next: Omit<T, 'versao'> & Partial<T>, old: T | undefined): T {
  if (!old) return { ...next, versao: 1 } as T;
  const { versao: _o, ...a } = old;
  const { versao: _n, ...b } = { ...old, ...next };
  return { ...old, ...next, versao: JSON.stringify(a) === JSON.stringify(b) ? old.versao : old.versao + 1 } as T;
}

export function fromForm(f: SubjectForm, original: Subject): Subject {
  return {
    ...original,
    nome: f.nome.trim(),
    ...(f.descricao.trim() ? { descricao: f.descricao.trim() } : { descricao: undefined }),
    fonte: f.fonte.trim(),
    app: f.app.trim(),
    donos: { negocio: f.donos.negocio.trim(), tecnico: f.donos.tecnico.trim() },
    metricas: f.metricas.map((m) =>
      versioned({ id: m.id.trim(), nome: m.nome.trim(), descricao: m.descricao.trim(), sinonimos: splitList(m.sinonimos), unidade: m.unidade.trim(), expressao: m.expressao.trim() }, original.metricas.find((x) => x.id === m.id)),
    ),
    dimensoes: f.dimensoes.map((d) =>
      versioned({ id: d.id.trim(), nome: d.nome.trim(), campo: d.campo.trim(), tipo: d.tipo, sensivel: d.sensivel, sinonimos: splitList(d.sinonimos) }, original.dimensoes.find((x) => x.id === d.id)),
    ),
    queries_validadas: f.queries.map((q) => {
      const old = original.queries_validadas.find((x) => x.id === q.id);
      return versioned(
        {
          id: q.id.trim(),
          exemplo: q.exemplo.trim(),
          exemplos: old?.exemplos ?? [],
          metricas: splitList(q.metricas),
          dimensoes: splitList(q.dimensoes),
          parametros: Object.fromEntries(q.parametros.map((p) => [p.nome.trim(), { dimensao: p.dimensao.trim(), obrigatorio: p.obrigatorio }])),
          certificada_por: q.certificada_por.trim() || undefined,
        },
        old,
      );
    }),
    roteiros: f.roteiros.map((r) => versioned({ id: r.id.trim(), quando: r.quando.trim(), passos: splitList(r.passos, /\n/) }, original.roteiros.find((x) => x.id === r.id))),
  } as Subject;
}

type Errors = Record<string, unknown> | undefined;
function errAt(errors: Errors, path: string): string | undefined {
  let node: unknown = errors;
  for (const p of path.split('.')) node = (node as Record<string, unknown> | undefined)?.[p];
  return (node as { message?: string } | undefined)?.message;
}

function ParamsEditor({ control, register, qi, errors, readOnly }: { control: Control<SubjectForm>; register: UseFormRegister<SubjectForm>; qi: number; errors: Errors; readOnly: boolean }) {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({ control, name: `queries.${qi}.parametros` });
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-[length:var(--lr-text-sm)] font-medium">{t('context.params')}</legend>
      {fields.map((f, pi) => (
        <div key={f.id} className="grid grid-cols-[1fr_1fr_auto_auto] items-end gap-2">
          <Field id={`q${qi}-p${pi}-nome`} label={t('context.form.param')} error={errAt(errors, `queries.${qi}.parametros.${pi}.nome`)}>
            <Input {...register(`queries.${qi}.parametros.${pi}.nome`)} disabled={readOnly} />
          </Field>
          <Field id={`q${qi}-p${pi}-dim`} label={t('context.form.paramDimension')} error={errAt(errors, `queries.${qi}.parametros.${pi}.dimensao`)}>
            <Input {...register(`queries.${qi}.parametros.${pi}.dimensao`)} disabled={readOnly} />
          </Field>
          <label className="flex h-[var(--field-height)] items-center gap-2 text-[length:var(--lr-text-sm)]">
            <Controller
              control={control}
              name={`queries.${qi}.parametros.${pi}.obrigatorio`}
              render={({ field }) => <Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={readOnly} />}
            />
            {t('context.form.paramRequired')}
          </label>
          <Button size="icon" variant="ghost" aria-label={`${t('common.remove')} ${t('context.form.param')} ${pi + 1}`} onClick={() => remove(pi)} disabled={readOnly}>
            <Trash2 />
          </Button>
        </div>
      ))}
      <div>
        <Button size="sm" variant="ghost" onClick={() => append({ nome: '', dimensao: '', obrigatorio: true })} disabled={readOnly}>
          <Plus />
          {t('context.form.addParam')}
        </Button>
      </div>
    </fieldset>
  );
}

function Section({ title, children, onAdd, addLabel, readOnly }: { title: string; children: React.ReactNode; onAdd: () => void; addLabel: string; readOnly: boolean }) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{title}</h3>
        <Button size="sm" onClick={onAdd} disabled={readOnly}>
          <Plus />
          {addLabel}
        </Button>
      </div>
      {children}
    </section>
  );
}

function ItemBox({ children, onRemove, label, readOnly }: { children: React.ReactNode; onRemove: () => void; label: string; readOnly: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="relative grid gap-3 rounded-[var(--lr-radius-md)] border border-border p-3 sm:grid-cols-2">
      {children}
      <div className="flex justify-end sm:col-span-2">
        <Button size="sm" variant="ghost" onClick={onRemove} disabled={readOnly} aria-label={`${t('common.remove')} ${label}`}>
          <Trash2 />
          {t('common.remove')}
        </Button>
      </div>
    </div>
  );
}

export function SubjectEditor({ subject, readOnly, onApply }: { subject: Subject; readOnly: boolean; onApply: (s: Subject) => void }) {
  const { t } = useTranslation();
  const schema = useMemo(() => subjectFormSchema(t), [t]);
  const form = useForm<SubjectForm>({ resolver: zodResolver(schema), defaultValues: toForm(subject), mode: 'onBlur' });
  const { register, control, handleSubmit, formState } = form;
  const errors = formState.errors as Errors;
  const metricas = useFieldArray({ control, name: 'metricas' });
  const dimensoes = useFieldArray({ control, name: 'dimensoes' });
  const queries = useFieldArray({ control, name: 'queries' });
  const roteiros = useFieldArray({ control, name: 'roteiros' });
  const p = `s-${subject.id}`;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('context.form.editSubject')}: {subject.nome}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-6" noValidate onSubmit={handleSubmit((f) => onApply(fromForm(f, subject)))}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field id={`${p}-nome`} label={t('context.form.name')} error={errAt(errors, 'nome')}>
              <Input {...register('nome')} disabled={readOnly} />
            </Field>
            <Field id={`${p}-fonte`} label={t('context.source')} error={errAt(errors, 'fonte')}>
              <Input {...register('fonte')} disabled={readOnly} />
            </Field>
            <Field id={`${p}-app`} label={t('context.app')} error={errAt(errors, 'app')}>
              <Input {...register('app')} disabled={readOnly} />
            </Field>
            <Field id={`${p}-negocio`} label={`${t('context.owners')}: ${t('context.business')}`} error={errAt(errors, 'donos.negocio')}>
              <Input type="email" {...register('donos.negocio')} disabled={readOnly} />
            </Field>
            <Field id={`${p}-tecnico`} label={`${t('context.owners')}: ${t('context.technical')}`} error={errAt(errors, 'donos.tecnico')}>
              <Input type="email" {...register('donos.tecnico')} disabled={readOnly} />
            </Field>
            <Field id={`${p}-descricao`} label={t('context.form.description')} className="sm:col-span-2">
              <Textarea rows={2} {...register('descricao')} disabled={readOnly} />
            </Field>
          </div>

          <Section title={t('context.metrics')} addLabel={t('context.form.addMetric')} readOnly={readOnly} onAdd={() => metricas.append({ id: '', nome: '', descricao: '', sinonimos: '', unidade: 'BRL', expressao: '' })}>
            {metricas.fields.map((f, i) => (
              <ItemBox key={f.id} label={`${t('context.metrics')} ${i + 1}`} onRemove={() => metricas.remove(i)} readOnly={readOnly}>
                <Field id={`${p}-m${i}-id`} label={t('context.form.id')} hint={t('context.form.idHelp')} error={errAt(errors, `metricas.${i}.id`)}>
                  <Input {...register(`metricas.${i}.id`)} disabled={readOnly} />
                </Field>
                <Field id={`${p}-m${i}-nome`} label={t('context.form.name')} error={errAt(errors, `metricas.${i}.nome`)}>
                  <Input {...register(`metricas.${i}.nome`)} disabled={readOnly} />
                </Field>
                <Field id={`${p}-m${i}-descricao`} label={t('context.form.description')} error={errAt(errors, `metricas.${i}.descricao`)} className="sm:col-span-2">
                  <Textarea rows={2} {...register(`metricas.${i}.descricao`)} disabled={readOnly} />
                </Field>
                <Field id={`${p}-m${i}-unidade`} label={t('context.unit')} hint={t('context.form.unitHelp')} error={errAt(errors, `metricas.${i}.unidade`)}>
                  <Input {...register(`metricas.${i}.unidade`)} disabled={readOnly} />
                </Field>
                <Field id={`${p}-m${i}-sinonimos`} label={t('context.synonyms')} hint={t('context.form.synonymsHelp')}>
                  <Input {...register(`metricas.${i}.sinonimos`)} disabled={readOnly} />
                </Field>
                <Field id={`${p}-m${i}-expressao`} label={t('context.expression')} hint={t('context.form.expressionHelp')} error={errAt(errors, `metricas.${i}.expressao`)} className="sm:col-span-2">
                  <Input className="font-mono" {...register(`metricas.${i}.expressao`)} disabled={readOnly} spellCheck={false} />
                </Field>
              </ItemBox>
            ))}
          </Section>

          <Section title={t('context.dimensions')} addLabel={t('context.form.addDimension')} readOnly={readOnly} onAdd={() => dimensoes.append({ id: '', nome: '', campo: '', tipo: 'texto', sensivel: false, sinonimos: '' })}>
            {dimensoes.fields.map((f, i) => (
              <ItemBox key={f.id} label={`${t('context.dimensions')} ${i + 1}`} onRemove={() => dimensoes.remove(i)} readOnly={readOnly}>
                <Field id={`${p}-d${i}-id`} label={t('context.form.id')} error={errAt(errors, `dimensoes.${i}.id`)}>
                  <Input {...register(`dimensoes.${i}.id`)} disabled={readOnly} />
                </Field>
                <Field id={`${p}-d${i}-nome`} label={t('context.form.name')} error={errAt(errors, `dimensoes.${i}.nome`)}>
                  <Input {...register(`dimensoes.${i}.nome`)} disabled={readOnly} />
                </Field>
                <Field id={`${p}-d${i}-campo`} label={t('context.field')} hint={t('context.form.fieldHelp')} error={errAt(errors, `dimensoes.${i}.campo`)}>
                  <Input {...register(`dimensoes.${i}.campo`)} disabled={readOnly} />
                </Field>
                <Field id={`${p}-d${i}-tipo`} label={t('context.type')}>
                  <NativeSelect {...register(`dimensoes.${i}.tipo`)} disabled={readOnly}>
                    <option value="texto">{t('context.dimTypes.texto')}</option>
                    <option value="periodo_mensal">{t('context.dimTypes.periodo_mensal')}</option>
                    <option value="ano">{t('context.dimTypes.ano')}</option>
                    <option value="data">{t('context.dimTypes.data')}</option>
                  </NativeSelect>
                </Field>
                <Field id={`${p}-d${i}-sinonimos`} label={t('context.synonyms')} hint={t('context.form.synonymsHelp')}>
                  <Input {...register(`dimensoes.${i}.sinonimos`)} disabled={readOnly} />
                </Field>
                <div className="flex items-center gap-2 self-end pb-2">
                  <Controller
                    control={control}
                    name={`dimensoes.${i}.sensivel`}
                    render={({ field }) => <Switch id={`${p}-d${i}-sensivel`} checked={field.value} onCheckedChange={field.onChange} disabled={readOnly} aria-describedby={`${p}-d${i}-sensivel-ajuda`} />}
                  />
                  <label htmlFor={`${p}-d${i}-sensivel`} className="text-[length:var(--lr-text-sm)] font-medium">
                    {t('context.sensitive')}
                  </label>
                  <span id={`${p}-d${i}-sensivel-ajuda`} className="sr-only">
                    {t('context.form.sensitiveHelp')}
                  </span>
                </div>
              </ItemBox>
            ))}
          </Section>

          <Section
            title={t('context.queries')}
            addLabel={t('context.form.addQuery')}
            readOnly={readOnly}
            onAdd={() => queries.append({ id: '', exemplo: '', metricas: '', dimensoes: '', certificada_por: '', parametros: [] })}
          >
            {queries.fields.map((f, i) => (
              <ItemBox key={f.id} label={`${t('context.queries')} ${i + 1}`} onRemove={() => queries.remove(i)} readOnly={readOnly}>
                <Field id={`${p}-q${i}-id`} label={t('context.form.id')} error={errAt(errors, `queries.${i}.id`)}>
                  <Input {...register(`queries.${i}.id`)} disabled={readOnly} />
                </Field>
                <Field id={`${p}-q${i}-cert`} label={t('context.form.certifiedBy')}>
                  <Input type="email" {...register(`queries.${i}.certificada_por`)} disabled={readOnly} />
                </Field>
                <Field id={`${p}-q${i}-exemplo`} label={t('context.form.example')} hint={t('context.form.exampleHelp')} error={errAt(errors, `queries.${i}.exemplo`)} className="sm:col-span-2">
                  <Input {...register(`queries.${i}.exemplo`)} disabled={readOnly} />
                </Field>
                <Field id={`${p}-q${i}-metricas`} label={t('context.form.queryMetrics')} error={errAt(errors, `queries.${i}.metricas`)}>
                  <Input {...register(`queries.${i}.metricas`)} disabled={readOnly} />
                </Field>
                <Field id={`${p}-q${i}-dimensoes`} label={t('context.form.queryGroupBy')}>
                  <Input {...register(`queries.${i}.dimensoes`)} disabled={readOnly} />
                </Field>
                <div className="sm:col-span-2">
                  <ParamsEditor control={control} register={register} qi={i} errors={errors} readOnly={readOnly} />
                </div>
              </ItemBox>
            ))}
          </Section>

          <Section title={t('context.playbooks')} addLabel={t('context.form.addPlaybook')} readOnly={readOnly} onAdd={() => roteiros.append({ id: '', quando: '', passos: '' })}>
            {roteiros.fields.map((f, i) => (
              <ItemBox key={f.id} label={`${t('context.playbooks')} ${i + 1}`} onRemove={() => roteiros.remove(i)} readOnly={readOnly}>
                <Field id={`${p}-r${i}-id`} label={t('context.form.id')} error={errAt(errors, `roteiros.${i}.id`)}>
                  <Input {...register(`roteiros.${i}.id`)} disabled={readOnly} />
                </Field>
                <Field id={`${p}-r${i}-quando`} label={t('context.form.when')} error={errAt(errors, `roteiros.${i}.quando`)}>
                  <Input {...register(`roteiros.${i}.quando`)} disabled={readOnly} />
                </Field>
                <Field id={`${p}-r${i}-passos`} label={t('context.form.steps')} error={errAt(errors, `roteiros.${i}.passos`)} className="sm:col-span-2">
                  <Textarea rows={3} {...register(`roteiros.${i}.passos`)} disabled={readOnly} />
                </Field>
              </ItemBox>
            ))}
          </Section>

          {Object.keys(formState.errors).length ? <Alert tone="danger" title={t('context.form.fixErrors')} /> : null}
          {!readOnly ? (
            <div>
              <Button type="submit" variant="primary">
                {t('context.form.apply')}
              </Button>
            </div>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}
